import React, { useMemo, useState } from 'react'
import {
    Box,
    ButtonBase,
    makeStyles,
    Theme,
    Typography,
} from '@material-ui/core'
import WorkIcon from '@material-ui/icons/Work'
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser'
import DescriptionIcon from '@material-ui/icons/Description'
import SecurityIcon from '@material-ui/icons/Security'
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd'
import DashboardIcon from '@material-ui/icons/Dashboard'
import { useUser } from '../../../../hooks/useUser'
import { useOrganizationCompanyList } from '../../../../hooks/useOrganizationCompanyList'
import { APARTMENT } from '../../../../helpers'
import { OrganizationCoverageOverview } from './OrganizationCoverageOverview'
import { OrganizationContracts } from './OrganizationContracts'
import { OrganizationDocuments } from './OrganizationDocuments'
import { OrganizationInsurance } from './OrganizationInsurance'
import { OrganizationW9Manager } from './OrganizationW9Manager'

type TabType =
    | 'overview'
    | 'contracts'
    | 'certificates'
    | 'documents'
    | 'insurance'
    | 'w9'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 104px)',
        width: '100%',
        backgroundColor: theme.palette.background.default,
    },
    header: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2, 3),
        display: 'flex',
        alignItems: 'center',
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    logo: {
        height: 32,
        width: 32,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.contrastText,
        fontWeight: 700,
        fontSize: '16px',
    },
    headerTitle: {
        fontSize: '20px',
        fontWeight: 700,
        letterSpacing: '-0.02em',
    },
    tabsContainer: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        position: 'sticky',
        top: 0,
        zIndex: 10,
    },
    tabsInner: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(4),
        padding: theme.spacing(0, 3),
    },
    tab: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(2, 0),
        fontSize: '14px',
        fontWeight: 500,
        borderBottom: '2px solid transparent',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        color: theme.palette.text.secondary,
        '&:hover': {
            color: theme.palette.text.primary,
            borderBottomColor: theme.palette.divider,
        },
    },
    activeTab: {
        color: theme.palette.primary.main,
        borderBottomColor: theme.palette.primary.main,
        '&:hover': {
            color: theme.palette.primary.main,
            borderBottomColor: theme.palette.primary.main,
        },
    },
    tabIcon: {
        fontSize: '18px',
    },
    mainContent: {
        flex: 1,
        overflowY: 'auto',
        padding: theme.spacing(1),
    },
}))

export const OrganizationContractManager = () => {
    const classes = useStyles()
    const { organization } = useUser()
    const { data: organizationCompanyList = [] } = useOrganizationCompanyList(
        organization,
        APARTMENT,
    )

    const [activeTab, setActiveTab] = useState<TabType>('overview')
    const [selectedTagId, setSelectedTagId] = useState<number | null>(null)

    const propertyList = useMemo(
        () =>
            organizationCompanyList.filter(
                (company) => company.company_type === APARTMENT,
            ),
        [organizationCompanyList],
    )

    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab)
    }

    const handleTagChange = (value: number | null) => {
        setSelectedTagId(value)
    }

    return (
        <Box className={classes.root}>
            {/* Header */}
            <Box className={classes.header}>
                <Box className={classes.headerLeft}>
                    <Box className={classes.logo}>C</Box>
                    <Typography className={classes.headerTitle}>
                        Contract Manager
                    </Typography>
                </Box>
            </Box>

            {/* Tab Navigation */}
            <Box className={classes.tabsContainer}>
                <Box className={classes.tabsInner}>
                    <ButtonBase
                        onClick={() => handleTabChange('overview')}
                        className={`${classes.tab} ${
                            activeTab === 'overview' ? classes.activeTab : ''
                        }`}
                    >
                        <DashboardIcon className={classes.tabIcon} />
                        <Typography variant="body2" component="span">
                            Overview
                        </Typography>
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => handleTabChange('contracts')}
                        className={`${classes.tab} ${
                            activeTab === 'contracts' ? classes.activeTab : ''
                        }`}
                    >
                        <WorkIcon className={classes.tabIcon} />
                        <Typography variant="body2" component="span">
                            Contracts
                        </Typography>
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => handleTabChange('certificates')}
                        className={`${classes.tab} ${
                            activeTab === 'certificates'
                                ? classes.activeTab
                                : ''
                        }`}
                    >
                        <VerifiedUserIcon className={classes.tabIcon} />
                        <Typography variant="body2" component="span">
                            Certificates
                        </Typography>
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => handleTabChange('documents')}
                        className={`${classes.tab} ${
                            activeTab === 'documents' ? classes.activeTab : ''
                        }`}
                    >
                        <DescriptionIcon className={classes.tabIcon} />
                        <Typography variant="body2" component="span">
                            Documents
                        </Typography>
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => handleTabChange('insurance')}
                        className={`${classes.tab} ${
                            activeTab === 'insurance' ? classes.activeTab : ''
                        }`}
                    >
                        <SecurityIcon className={classes.tabIcon} />
                        <Typography variant="body2" component="span">
                            Insurance
                        </Typography>
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => handleTabChange('w9')}
                        className={`${classes.tab} ${
                            activeTab === 'w9' ? classes.activeTab : ''
                        }`}
                    >
                        <AssignmentIndIcon className={classes.tabIcon} />
                        <Typography variant="body2" component="span">
                            W-9s
                        </Typography>
                    </ButtonBase>
                </Box>
            </Box>

            {/* Main Content Area */}
            <Box className={classes.mainContent}>
                {activeTab === 'overview' && (
                    <OrganizationCoverageOverview
                        organization={organization}
                        selectedTagId={selectedTagId}
                        onSelectedTagIdChange={handleTagChange}
                    />
                )}
                {activeTab === 'contracts' && (
                    <OrganizationContracts
                        organization={organization}
                        selectedTagId={selectedTagId}
                        onSelectedTagIdChange={handleTagChange}
                        propertyList={propertyList}
                    />
                )}
                {activeTab === 'certificates' && (
                    <OrganizationContracts
                        organization={organization}
                        selectedTagId={selectedTagId}
                        onSelectedTagIdChange={handleTagChange}
                        propertyList={propertyList}
                        variant="certificates"
                    />
                )}
                {activeTab === 'documents' && (
                    <OrganizationDocuments
                        organization={organization}
                        selectedTagId={selectedTagId}
                        onSelectedTagIdChange={handleTagChange}
                        propertyList={propertyList}
                    />
                )}
                {activeTab === 'insurance' && (
                    <OrganizationInsurance
                        organization={organization}
                        selectedTagId={selectedTagId}
                        onSelectedTagIdChange={handleTagChange}
                        propertyList={propertyList}
                    />
                )}
                {activeTab === 'w9' && (
                    <OrganizationW9Manager
                        organization={organization}
                        selectedTagId={selectedTagId}
                        onSelectedTagIdChange={handleTagChange}
                        propertyList={propertyList}
                    />
                )}
            </Box>
        </Box>
    )
}
